import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableSlotGroup,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import {
  GalleryGrid,
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';
import { orderGalleryPhotos, type GalleryScope } from '../utils/galleryOrder';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Explore Neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

type GalleryTab = 'All' | GalleryScope;

// Fixed tab order. "All" is Community (exterior → outdoor → indoor) followed
// by Apartment (kitchen → … → other interior features).
const SCOPES: GalleryScope[] = ['Community', 'Apartment'];

// Only these photo categories may appear in the gallery. Anything else (Logo,
// Lifestyle, Floorplan, Other, …) is pruned entirely — not under "All", not as
// its own tab, and not in the lightbox. Keyed lowercase for case-insensitive
// matching; values are the canonical display label used for the tab.
const ALLOWED_CATEGORIES: Record<string, GalleryScope> = {
  apartment: 'Apartment',
  community: 'Community',
};

// ============================================================================
// Component
// ============================================================================

/**
 * Filterable photo gallery. Tabs are All / Community / Apartment (a tab is
 * hidden only when it has no photos). Each scope is ordered by subject via
 * `orderGalleryPhotos`, so similar shots sit together. The grid is static at
 * every breakpoint; clicking a photo opens the lightbox scoped to the visible
 * set. A primary CTA sits beneath the grid.
 */
export function GalleryPhotos() {
  const cta = useSlotGroup('galleryphotos.cta', ctaDef);

  const rawImages = useCollectionBinding<GalleryImage>('galleryphotos.images', {
    collection: 'photo_gallery',
  });

  // Normalize + prune the gallery source:
  //  - some photo_gallery items only carry `original_url` → fall back to it;
  //  - drop any entry with no usable image URL at all;
  //  - keep only photos in an allowed category (Apartment, Community). Any other
  //    category — Logo, Lifestyle, Floorplan, Other, … — is removed entirely, so
  //    it never appears under "All", as its own tab, or in the lightbox. The
  //    title is also normalized to its canonical label.
  const images = useMemo(
    () =>
      rawImages
        .map((img) => ({ ...img, image: img.image || img.original_url || '' }))
        .filter((img) => img.image)
        .filter((img) => ALLOWED_CATEGORIES[(img.title || '').trim().toLowerCase()])
        .map((img) => ({
          ...img,
          title: ALLOWED_CATEGORIES[(img.title || '').trim().toLowerCase()],
        })),
    [rawImages],
  );

  const byScope = useMemo(() => {
    const result = {} as Record<GalleryScope, GalleryImage[]>;
    for (const scope of SCOPES) {
      result[scope] = orderGalleryPhotos(
        images.filter((img) => img.title === scope),
        scope,
      );
    }
    return result;
  }, [images]);

  const categories = SCOPES.filter((scope) => byScope[scope].length > 0);
  const tabs: GalleryTab[] = ['All', ...categories];

  const [activeTab, setActiveTab] = useState<GalleryTab>('All');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Guard against a stale tab if the underlying categories change.
  const effectiveTab: GalleryTab =
    activeTab === 'All' || categories.includes(activeTab) ? activeTab : 'All';

  const visibleImages = useMemo(
    () =>
      effectiveTab === 'All'
        ? SCOPES.flatMap((scope) => byScope[scope])
        : byScope[effectiveTab],
    [byScope, effectiveTab],
  );

  if (images.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained pt-[20px] pb-12 md:pb-16">
        {/* Category filter tabs — only meaningful when photos span >1 category */}
        {categories.length > 1 && (
          <div className="gallery-tabs" role="tablist" aria-label="Gallery categories">
            {tabs.map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={effectiveTab === tab}
                className={`gallery-tab${effectiveTab === tab ? ' is-active' : ''}`}
                onClick={() => {
                  setActiveTab(tab);
                  setSelectedIndex(null);
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        )}

        <EditableCollectionBinding bindingPath="galleryphotos.images" label="Gallery Photos">
          <GalleryGrid images={visibleImages} onSelect={setSelectedIndex} />
        </EditableCollectionBinding>

        <div className="gallery-photos__cta-row">
          <EditableSlotGroup groupPath="galleryphotos.cta" definition={ctaDef}>
            <a
              href={cta.url || '/neighborhood'}
              target={cta._target || '_self'}
              className="gallery-photos__cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
            >
              {cta.label || 'Explore Neighborhood'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <GalleryLightbox
        images={visibleImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />

      <style>{`
        /* ── Category filter tabs ─────────────────────────────── */
        .gallery-tabs {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem 0.75rem;
          margin-bottom: 2rem;
        }

        /* Pill CTA-style filter chips — outlined by default, filled primary when
           active, matching the site-wide pill treatment. Tablet/mobile use the
           smaller size; desktop (>=1024px) steps up font + padding + tracking. */
        .gallery-tab {
          background: none;
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
          padding: 0.5rem 1.25rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          white-space: nowrap;
          color: var(--vibe-color-primary);
          cursor: pointer;
          transition: color 0.2s, background-color 0.2s, border-color 0.2s,
            box-shadow 0.2s, transform 0.2s;
        }
        .gallery-tab:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 10%, transparent);
        }
        .gallery-tab.is-active {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .gallery-tab.is-active:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        @media (min-width: 1024px) {
          .gallery-tab {
            font-size: 1rem;
            padding: 0.625rem 1.75rem;
            letter-spacing: 0.15em;
          }
        }
        /* Mobile only: pack the pills closer and shrink them a touch, with a
           little side padding so wrapped rows keep breathing room at the edges. */
        @media (max-width: 767px) {
          .gallery-tabs {
            gap: 0.4rem;
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          .gallery-tab {
            font-size: 0.75rem;
            padding: 0.375rem 0.85rem;
            letter-spacing: 0.08em;
          }
        }

        /* ── Bottom CTA ───────────────────────────────────────── */
        .gallery-photos__cta-row {
          display: flex;
          justify-content: center;
          margin-top: 20px;
        }
        @media (min-width: 768px) {
          .gallery-photos__cta-row {
            margin-top: 35px;
          }
        }

        /* Solid primary pill — matches the site-wide CTA treatment. */
        .gallery-photos__cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
        }
        .gallery-photos__cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryPhotos',
  displayName: 'Photo Gallery',
  tokens: {},
  slotGroups: {
    'galleryphotos.cta': ctaDef,
  },
  collectionBindings: {
    'galleryphotos.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description:
        'Photos from the property photo gallery. Only Community and Apartment photos are shown; other categories (Logo, Lifestyle, Floorplan, …) are excluded. Tabs are "All", "Community", and "Apartment". Photos are auto-sorted by subject from their alt text / description: Community = exterior, outdoor amenities, indoor amenities; Apartment = kitchen, islands, living, dining, bedrooms, bathrooms, closets, laundry, patios, other. Staged photos lead; blurry / dirty / empty-closet photos are dropped.',
    },
  },
};

(GalleryPhotos as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryPhotos;
